<template>
  <div class="blog-post">
    <!-- Hero Section -->
    <section class="bg-bleumarine text-white py-12">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto">
          <router-link to="/blog" class="inline-flex items-center text-turquoise hover:text-white mb-6 transition">
            <ArrowLeft class="w-5 h-5 mr-2" :stroke="'#fff'" />
            Retour au blog
          </router-link>
          <h1 v-if="article" class="text-3xl md:text-4xl font-bold mb-4">{{ article.title }}</h1>
          <div v-if="article" class="flex items-center space-x-4 text-turquoise">
            <span class="flex items-center">
              <Calendar class="w-5 h-5 mr-2" :stroke="'#fff'" />
              {{ article.date }}
            </span>
            <span class="flex items-center">
              <User class="w-5 h-5 mr-2" :stroke="'#fff'" />
              {{ article.author }}
            </span>
            <span class="flex items-center">
              <Tag class="w-5 h-5 mr-2" :stroke="'#fff'" />
              {{ article.category }}
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- Article Content -->
    <section v-if="article" class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto">
          <!-- Featured Image -->
          <div class="bg-gray-200 h-64 md:h-96 rounded-lg mb-8 flex items-center justify-center">
            <Newspaper class="w-24 h-24 text-gray-400" :stroke="'#fff'" />
          </div>

          <!-- Article Body -->
          <div class="prose prose-lg max-w-none">
            <p class="text-xl text-gray-700 mb-6 leading-relaxed">
              {{ article.excerpt }}
            </p>
            <div class="text-gray-700 space-y-4">
              <p>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
              </p>
              <p>
                Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
              </p>
              <h3 class="text-2xl font-bold mt-8 mb-4">Points clés</h3>
              <ul class="list-disc pl-6 space-y-2">
                <li>Premier point important de l'article</li>
                <li>Deuxième point essentiel à retenir</li>
                <li>Troisième élément clé de la discussion</li>
                <li>Quatrième aspect à considérer</li>
              </ul>
              <p>
                Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.
              </p>
              <h3 class="text-2xl font-bold mt-8 mb-4">Conclusion</h3>
              <p>
                Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.
              </p>
            </div>
          </div>

          <!-- Share Buttons -->
          <div class="mt-12 pt-8 border-t">
            <h3 class="text-lg font-semibold mb-4">Partager cet article</h3>
            <div class="flex space-x-4">
              <button class="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition">
                <Share2 class="w-5 h-5" :stroke="'#fff'" />
              </button>
              <button class="w-10 h-10 bg-turquoise text-white rounded-full flex items-center justify-center hover:opacity-80 transition">
                <Mail class="w-5 h-5" :stroke="'#fff'" />
              </button>
              <button class="w-10 h-10 bg-violet text-white rounded-full flex items-center justify-center hover:opacity-80 transition">
                <Link class="w-5 h-5" :stroke="'#fff'" />
              </button>
            </div>
          </div>

          <!-- Related Articles -->
          <div class="mt-12 pt-8 border-t">
            <h3 class="text-lg font-semibold mb-6">Articles similaires</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div v-for="related in relatedArticles" :key="related.id" class="card cursor-pointer hover:shadow-lg transition">
                <div class="bg-gray-200 h-32 rounded-lg mb-4 flex items-center justify-center">
                  <Newspaper class="w-12 h-12 text-gray-400" :stroke="'#fff'" />
                </div>
                <span class="text-sm text-turquoise font-medium">{{ related.category }}</span>
                <h4 class="text-lg font-semibold mt-2 mb-2">{{ related.title }}</h4>
                <p class="text-gray-600 text-sm">{{ related.excerpt }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Article Not Found -->
    <section v-else class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto text-center">
          <h1 class="text-3xl font-bold mb-4">Article non trouvé</h1>
          <p class="text-gray-600 mb-8">L'article que vous recherchez n'existe pas ou a été supprimé.</p>
          <router-link to="/blog" class="btn-primary inline-block">
            Retour au blog
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { ArrowLeft, Calendar, User, Tag, Newspaper, Share2, Mail, Link } from 'lucide-vue-next'

const route = useRoute()
const article = ref(null)
const allArticles = [
  {
    id: 1,
    title: 'Les meilleures pratiques de sécurité cloud en 2024',
    excerpt: 'Découvrez comment sécuriser vos infrastructures cloud avec les dernières recommandations de sécurité et protéger vos données sensibles.',
    category: 'Cloud',
    author: 'Kouamé Jean',
    date: '15 Jan 2024'
  },
  {
    id: 2,
    title: 'Introduction à DevOps pour les entreprises',
    excerpt: 'Comprendre les principes DevOps et comment les appliquer dans votre organisation pour améliorer la collaboration et la livraison logicielle.',
    category: 'DevOps',
    author: 'Touré Aminata',
    date: '10 Jan 2024'
  },
  {
    id: 3,
    title: 'Cybersécurité : protéger votre entreprise contre les ransomwares',
    excerpt: 'Guide complet pour se prémunir contre les attaques par ransomware et mettre en place une stratégie de défense efficace.',
    category: 'Cyber sécurité',
    author: 'Diaby Fatou',
    date: '5 Jan 2024'
  },
  {
    id: 4,
    title: 'Les fondamentaux du développement web moderne',
    excerpt: 'Apprenez les bases du développement web avec les technologies actuelles : HTML5, CSS3, JavaScript et les frameworks populaires.',
    category: 'Développement',
    author: 'Koffi Emmanuel',
    date: '28 Déc 2023'
  },
  {
    id: 5,
    title: 'Gestion de serveur Linux pour débutants',
    excerpt: 'Un guide pratique pour administrer vos serveurs Linux : commandes essentielles, gestion des utilisateurs et surveillance système.',
    category: 'Gestion serveur',
    author: 'Kouamé Jean',
    date: '20 Déc 2023'
  },
  {
    id: 6,
    title: 'Introduction à l\'Intelligence Artificielle',
    excerpt: 'Découvrez les concepts fondamentaux de l\'IA, le machine learning et comment ces technologies transforment les entreprises.',
    category: 'Intelligence Artificielle',
    author: 'Touré Aminata',
    date: '15 Déc 2023'
  },
  {
    id: 7,
    title: 'Data Science : analyser et visualiser vos données',
    excerpt: 'Maîtrisez les outils et techniques de data science pour transformer vos données en insights décisionnels pertinents.',
    category: 'Data Science',
    author: 'Diaby Fatou',
    date: '10 Déc 2023'
  },
  {
    id: 8,
    title: 'Architecture réseau : concevoir une infrastructure robuste',
    excerpt: 'Les principes clés pour concevoir et mettre en place une architecture réseau fiable et performante pour votre entreprise.',
    category: 'Réseaux',
    author: 'Koffi Emmanuel',
    date: '5 Déc 2023'
  },
  {
    id: 9,
    title: 'Devenir Data Analyste : compétences et outils',
    excerpt: 'Le parcours pour devenir data analyste : compétences requises, outils à maîtriser et perspectives de carrière.',
    category: 'Data Analyste',
    author: 'Kouamé Jean',
    date: '1 Déc 2023'
  }
]

const relatedArticles = computed(() => {
  if (!article.value) return []
  return allArticles
    .filter(a => a.id !== article.value.id && a.category === article.value.category)
    .slice(0, 2)
})

onMounted(() => {
  const articleId = parseInt(route.params.id)
  article.value = allArticles.find(a => a.id === articleId)
})
</script>
